import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Button,
  Card,
  CardContent,
  Chip,
  Grid,
  CircularProgress,
  Alert,
  IconButton,
  Menu,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  useTheme,
  alpha,
  Divider,
  Paper,
} from '@mui/material';
import {
  Add,
  MoreVert,
  Edit,
  Delete,
  Visibility,
  Share,
  Public as PublishIcon,
  Unpublished as UnpublishIcon,
  ContentCopy as CopyIcon,
  Telegram as TelegramIcon
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import quizAPI from '../../services/quizAPI';
import { categoryAPI } from '../../services/api';
import { FormControl, InputLabel, Select } from '@mui/material';

const QuizList = () => {
  const navigate = useNavigate();
  const theme = useTheme();
  const [quizzes, setQuizzes] = useState([]);
  const [categories, setCategories] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [anchorEl, setAnchorEl] = useState(null);
  const [selectedQuiz, setSelectedQuiz] = useState(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [shareDialogOpen, setShareDialogOpen] = useState(false);
  const [telegramLink, setTelegramLink] = useState('');

  useEffect(() => {
    fetchCategories();
    fetchQuizzes();
  }, []);

  useEffect(() => {
    fetchQuizzes();
  }, [selectedCategory]);

  const fetchCategories = async () => {
    try {
      const response = await categoryAPI.getAll();
      setCategories(response.data.categories || []);
    } catch (err) {
      console.error('Erreur lors du chargement des catégories:', err);
    }
  };

  const fetchQuizzes = async () => {
    try {
      setLoading(true);
      setError('');
      const params = selectedCategory ? { category: selectedCategory } : {};
      const response = await quizAPI.getAll(params);
      console.log('📚 Quiz fetched:', response);
      setQuizzes(response.quizzes || response.data?.quizzes || []);
    } catch (err) {
      const errorMsg = err.response?.data?.message || 'Erreur lors du chargement des quiz';
      setError(errorMsg);
      toast.error(errorMsg);
      console.error('Quiz list error:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleMenuOpen = (event, quiz) => {
    setAnchorEl(event.currentTarget);
    setSelectedQuiz(quiz);
  };

  const handleMenuClose = (resetQuiz = false) => {
    setAnchorEl(null);
    if (resetQuiz) {
      setSelectedQuiz(null);
    }
  };

  const handleOpenDeleteDialog = () => {
    console.log('🗑️ Opening delete dialog for quiz:', selectedQuiz?.title);
    setAnchorEl(null); // Fermer le menu
    setDeleteDialogOpen(true); // Ouvrir le dialog
    // selectedQuiz reste défini pour être utilisé dans le dialog
  };

  const handleCloseDeleteDialog = () => {
    setDeleteDialogOpen(false);
    setSelectedQuiz(null); // Réinitialiser uniquement à la fermeture du dialog
  };

  const handleConfirmDelete = async () => {
    if (!selectedQuiz) {
      console.warn('⚠️ No quiz selected for deletion');
      return;
    }
    
    console.log('🗑️ Deleting quiz:', selectedQuiz._id, selectedQuiz.title);
    setDeleting(true);
    
    try {
      const response = await quizAPI.delete(selectedQuiz._id);
      console.log('✅ Delete response:', response);
      
      let successMessage = response.message || 'Quiz supprimé avec succès';
      if (response.deletedMedia && response.deletedMedia > 0) {
        successMessage += ` (${response.deletedMedia} média${response.deletedMedia > 1 ? 'x' : ''} supprimé${response.deletedMedia > 1 ? 's' : ''})`;
      }
      if (response.deletedAttempts && response.deletedAttempts > 0) {
        successMessage += ` (${response.deletedAttempts} tentative${response.deletedAttempts > 1 ? 's' : ''} supprimée${response.deletedAttempts > 1 ? 's' : ''})`;
      }
      toast.success(successMessage);
      
      handleCloseDeleteDialog();
      await fetchQuizzes();
    } catch (err) {
      console.error('❌ Delete error:', err);
      const errorMsg = err.response?.data?.message || 'Erreur lors de la suppression du quiz';
      toast.error(errorMsg);
      setError(errorMsg);
    } finally {
      setDeleting(false);
    }
  };

  const handleTogglePublish = async () => {
    if (!selectedQuiz) return;
    
    const quizId = selectedQuiz._id;
    const quizTitle = selectedQuiz.title;
    
    // Fermer le menu immédiatement
    setAnchorEl(null);
    
    try {
      console.log('📢 Toggling publish for quiz:', quizId);
      const response = await quizAPI.togglePublish(quizId);
      console.log('✅ Publish response:', response);
      toast.success(response.message || 'Statut du quiz mis à jour');
      await fetchQuizzes();
    } catch (err) {
      const errorMsg = err.response?.data?.message || 'Erreur lors de la publication du quiz';
      toast.error(errorMsg);
      setError(errorMsg);
      console.error('Publish error:', err);
    } finally {
      setSelectedQuiz(null);
    }
  };

  const handleOpenShareDialog = () => {
    if (!selectedQuiz) return;
    
    console.log('📤 Ouverture dialog partage pour:', selectedQuiz);
    
    // URL pour le partage public
    // Pour tester en local : utiliser localhost
    // En production : remplacer par votre domaine (ex: https://votre-domaine.com)
    const publicUrl = window.location.origin; // http://localhost:3000 en dev
    const link = `${publicUrl}/quiz/${selectedQuiz._id}/preview`;
    
    console.log('🔗 Lien généré:', link);
    
    setTelegramLink(link);
    setShareDialogOpen(true);
    setAnchorEl(null);
    
    // Copier automatiquement le lien dans le presse-papiers (avec fallback)
    // Utiliser setTimeout pour s'assurer que le dialog est rendu
    setTimeout(async () => {
      const success = await copyToClipboard(link);
      if (success) {
        toast.success('Lien copié automatiquement !');
      } else {
        // Ne pas afficher d'erreur à l'ouverture, l'utilisateur pourra utiliser le bouton
        console.warn('Impossible de copier automatiquement, l\'utilisateur devra utiliser le bouton Copier');
      }
    }, 300);
  };

  const handleCloseShareDialog = () => {
    setShareDialogOpen(false);
    setTelegramLink('');
    setSelectedQuiz(null);
  };

  // Fonction de copie avec fallback pour HTTP (production)
  const copyToClipboard = async (text) => {
    // Méthode moderne (nécessite HTTPS ou localhost)
    if (navigator.clipboard && window.isSecureContext) {
      try {
        await navigator.clipboard.writeText(text);
        return true;
      } catch (err) {
        console.warn('Clipboard API failed, trying fallback:', err);
      }
    }

    // Fallback pour HTTP (production) - méthode plus fiable
    return new Promise((resolve) => {
      // Créer un élément input temporaire (plus fiable que textarea)
      const input = document.createElement('input');
      input.value = text;
      input.style.position = 'fixed';
      input.style.top = '0';
      input.style.left = '0';
      input.style.width = '1px';
      input.style.height = '1px';
      input.style.padding = '0';
      input.style.border = 'none';
      input.style.margin = '0';
      input.style.opacity = '0';
      input.style.pointerEvents = 'none';
      input.setAttribute('readonly', '');
      input.setAttribute('aria-hidden', 'true');
      
      document.body.appendChild(input);
      
      // Sélectionner le texte
      input.focus();
      input.select();
      input.setSelectionRange(0, text.length);
      
      // Utiliser execCommand avec gestion d'erreur
      let successful = false;
      try {
        // execCommand nécessite une interaction utilisateur directe
        // Si on est dans un setTimeout, ça peut ne pas fonctionner
        successful = document.execCommand('copy');
      } catch (err) {
        console.error('execCommand error:', err);
        successful = false;
      }
      
      // Nettoyer immédiatement
      document.body.removeChild(input);
      
      // Retourner le résultat
      resolve(successful);
    });
  };

  const handleCopyLink = () => {
    // Créer un input temporaire pour la copie (méthode la plus fiable)
    const input = document.createElement('input');
    input.value = telegramLink;
    input.style.position = 'fixed';
    input.style.top = '0';
    input.style.left = '0';
    input.style.width = '1px';
    input.style.height = '1px';
    input.style.opacity = '0';
    input.style.pointerEvents = 'none';
    
    document.body.appendChild(input);
    input.focus();
    input.select();
    input.setSelectionRange(0, telegramLink.length);
    
    try {
      const successful = document.execCommand('copy');
      document.body.removeChild(input);
      
      if (successful) {
        toast.success('Lien copié dans le presse-papiers !');
      } else {
        // Si execCommand échoue, essayer navigator.clipboard
        if (navigator.clipboard && window.isSecureContext) {
          navigator.clipboard.writeText(telegramLink).then(() => {
            toast.success('Lien copié dans le presse-papiers !');
          }).catch(() => {
            selectTextForManualCopy('[data-link-text]', 'lien');
          });
        } else {
          selectTextForManualCopy('[data-link-text]', 'lien');
        }
      }
    } catch (err) {
      document.body.removeChild(input);
      console.error('Copy error:', err);
      selectTextForManualCopy('[data-link-text]', 'lien');
    }
  };

  const selectTextForManualCopy = (selector, type) => {
    const element = document.querySelector(selector);
    if (element) {
      const range = document.createRange();
      range.selectNodeContents(element);
      const selection = window.getSelection();
      selection.removeAllRanges();
      selection.addRange(range);
      
      toast.info(`Le ${type} est sélectionné. Appuyez sur Ctrl+C (ou Cmd+C sur Mac) pour copier.`, {
        duration: 5000
      });
    } else {
      toast.error(`Impossible de copier. Veuillez sélectionner et copier manuellement le ${type} ci-dessus.`);
    }
  };

  const handleCopyMessage = () => {
    if (!selectedQuiz || !telegramLink) return;
    
    // Créer un message formaté pour le partage
    const message = `🎯 **${selectedQuiz.title}**

📝 ${selectedQuiz.description || 'Testez vos connaissances !'}

📊 Informations :
• ${selectedQuiz.questions?.length || 0} questions
• Durée : ${selectedQuiz.duration} minutes
• Note de passage : ${selectedQuiz.passingScore}%
• Tentatives autorisées : ${selectedQuiz.maxAttempts}

🔗 Passer le quiz :
${telegramLink}

✅ Créez votre compte gratuit et commencez dès maintenant !`;

    // Créer un input temporaire pour la copie (méthode la plus fiable)
    const input = document.createElement('input');
    input.value = message;
    input.style.position = 'fixed';
    input.style.top = '0';
    input.style.left = '0';
    input.style.width = '1px';
    input.style.height = '1px';
    input.style.opacity = '0';
    input.style.pointerEvents = 'none';
    
    document.body.appendChild(input);
    input.focus();
    input.select();
    input.setSelectionRange(0, message.length);
    
    try {
      const successful = document.execCommand('copy');
      document.body.removeChild(input);
      
      if (successful) {
        toast.success('Message copié ! Collez-le dans votre groupe 📋');
      } else {
        // Si execCommand échoue, essayer navigator.clipboard
        if (navigator.clipboard && window.isSecureContext) {
          navigator.clipboard.writeText(message).then(() => {
            toast.success('Message copié ! Collez-le dans votre groupe 📋');
          }).catch(() => {
            selectTextForManualCopy('[data-message-text]', 'message');
          });
        } else {
          selectTextForManualCopy('[data-message-text]', 'message');
        }
      }
    } catch (err) {
      document.body.removeChild(input);
      console.error('Copy error:', err);
      selectTextForManualCopy('[data-message-text]', 'message');
    }
  };

  const handleShareToTelegram = () => {
    console.log('🚀 handleShareToTelegram appelé', { selectedQuiz, telegramLink });
    
    if (!selectedQuiz || !telegramLink) {
      console.error('❌ Données manquantes:', { selectedQuiz, telegramLink });
      toast.error('Erreur : Données manquantes pour le partage');
      return;
    }
    
    const message = `🎯 **Quiz disponible : ${selectedQuiz.title}**\n\n${selectedQuiz.description || 'Testez vos connaissances avec ce quiz !'}\n\n📱 Cliquez sur le lien pour commencer :\n${telegramLink}`;
    
    // Utiliser l'API de partage Telegram
    const telegramShareUrl = `https://t.me/share/url?url=${encodeURIComponent(telegramLink)}&text=${encodeURIComponent(message)}`;
    
    console.log('🔗 URL de partage:', telegramShareUrl);
    
    // Ouvrir Telegram pour partager
    window.open(telegramShareUrl, '_blank');
    
    toast.success('Telegram s\'ouvre pour le partage...');
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '50vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4">
          Gestion des Quiz
        </Typography>
        <Button
          variant="contained"
          startIcon={<Add />}
          onClick={() => navigate('/quiz/create')}
        >
          Créer un Quiz
        </Button>
      </Box>

      {/* Filtre par catégorie */}
      <Box sx={{ mb: 3 }}>
        <FormControl fullWidth sx={{ maxWidth: 300 }}>
          <InputLabel id="category-filter-label">Filtrer par catégorie</InputLabel>
          <Select
            labelId="category-filter-label"
            id="category-filter"
            value={selectedCategory}
            label="Filtrer par catégorie"
            onChange={(e) => setSelectedCategory(e.target.value)}
          >
            <MenuItem value="">
              <em>Toutes les catégories</em>
            </MenuItem>
            {categories.map((category) => (
              <MenuItem key={category.id} value={category.id}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Box
                    sx={{
                      width: 12,
                      height: 12,
                      borderRadius: '50%',
                      bgcolor: category.color || '#1976d2'
                    }}
                  />
                  {category.name}
                  {category.quizCount !== undefined && (
                    <Typography variant="caption" color="text.secondary" sx={{ ml: 1 }}>
                      ({category.quizCount})
                    </Typography>
                  )}
                </Box>
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      <Grid container spacing={3}>
        {quizzes.map((quiz) => (
          <Grid item xs={12} md={6} lg={4} key={quiz._id}>
            <Card>
              <CardContent>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                  <Box sx={{ flexGrow: 1 }}>
                    <Typography variant="h6" gutterBottom>
                      {quiz.title}
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                      {quiz.description}
                    </Typography>
                    <Box sx={{ display: 'flex', gap: 1, mb: 2, flexWrap: 'wrap' }}>
                      <Chip
                        label={quiz.status}
                        color={quiz.status === 'published' ? 'success' : 'default'}
                        size="small"
                      />
                      <Chip
                        label={`${quiz.duration} min`}
                        variant="outlined"
                        size="small"
                      />
                      {quiz.category && (
                        <Chip
                          label={quiz.category.name}
                          size="small"
                          sx={{
                            bgcolor: quiz.category.color || '#1976d2',
                            color: 'white',
                            fontWeight: 'bold'
                          }}
                        />
                      )}
                    </Box>
                    <Typography variant="body2" color="text.secondary">
                      {quiz.questions?.length || 0} questions
                    </Typography>
                  </Box>
                  <IconButton
                    onClick={(e) => handleMenuOpen(e, quiz)}
                    size="small"
                  >
                    <MoreVert />
                  </IconButton>
                </Box>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      {quizzes.length === 0 && !loading && (
        <Card>
          <CardContent sx={{ textAlign: 'center', py: 4 }}>
            <Typography variant="h6" color="text.secondary" gutterBottom>
              Aucun quiz créé
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Commencez par créer votre premier quiz
            </Typography>
            <Button
              variant="contained"
              startIcon={<Add />}
              onClick={() => navigate('/quiz/create')}
            >
              Créer un Quiz
            </Button>
          </CardContent>
        </Card>
      )}

      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={() => handleMenuClose(true)}
      >
        {selectedQuiz?.status === 'published' && (
          <MenuItem onClick={() => { 
            const id = selectedQuiz._id;
            handleMenuClose(true); 
            navigate(`/quiz/${id}`); 
          }}>
            <Visibility sx={{ mr: 1 }} />
            Passer le Quiz
          </MenuItem>
        )}
        <MenuItem 
          onClick={() => { 
            const id = selectedQuiz._id;
            handleMenuClose(true); 
            navigate(`/quiz/${id}/edit`);
          }}
        >
          <Edit sx={{ mr: 1 }} />
          Modifier
          {selectedQuiz?.attemptsCount > 0 && (
            <Typography variant="caption" sx={{ ml: 1, fontStyle: 'italic', color: 'text.secondary' }}>
              ({selectedQuiz.attemptsCount} tentative{selectedQuiz.attemptsCount > 1 ? 's' : ''})
            </Typography>
          )}
        </MenuItem>
        <MenuItem onClick={handleTogglePublish}>
          {selectedQuiz?.status === 'published' ? (
            <>
              <UnpublishIcon sx={{ mr: 1 }} />
              Dépublier
            </>
          ) : (
            <>
              <PublishIcon sx={{ mr: 1 }} />
              Publier
            </>
          )}
        </MenuItem>
        {selectedQuiz?.status === 'published' && (
          <MenuItem onClick={handleOpenShareDialog}>
            <Share sx={{ mr: 1 }} />
            Partager le quiz
          </MenuItem>
        )}
        <MenuItem 
          onClick={handleOpenDeleteDialog} 
          sx={{ color: 'error.main' }}
        >
          <Delete sx={{ mr: 1 }} />
          Supprimer
          {selectedQuiz?.attemptsCount > 0 && (
            <Typography variant="caption" sx={{ ml: 1, fontStyle: 'italic', color: 'text.secondary' }}>
              ({selectedQuiz.attemptsCount} tentative{selectedQuiz.attemptsCount > 1 ? 's' : ''})
            </Typography>
          )}
        </MenuItem>
      </Menu>

      {/* Dialog de confirmation de suppression */}
      <Dialog
        open={deleteDialogOpen}
        onClose={handleCloseDeleteDialog}
      >
        <DialogTitle>
          Confirmer la suppression
        </DialogTitle>
        <DialogContent>
          <DialogContentText>
            {selectedQuiz?.attemptsCount > 0 && (
              <Alert severity="warning" sx={{ mb: 2 }}>
                <strong>Attention : Ce quiz a {selectedQuiz.attemptsCount} tentative{selectedQuiz.attemptsCount > 1 ? 's' : ''} associée{selectedQuiz.attemptsCount > 1 ? 's' : ''}.</strong>
                <br />
                La suppression supprimera également toutes les tentatives et leurs résultats.
              </Alert>
            )}
            Êtes-vous sûr de vouloir supprimer le quiz "<strong>{selectedQuiz?.title}</strong>" ?
            {selectedQuiz?.questions?.length > 0 && (
              <span> Ce quiz contient {selectedQuiz.questions.length} question{selectedQuiz.questions.length > 1 ? 's' : ''}.</span>
            )}
            {selectedQuiz?.attemptsCount > 0 && (
              <span> Il a {selectedQuiz.attemptsCount} tentative{selectedQuiz.attemptsCount > 1 ? 's' : ''} associée{selectedQuiz.attemptsCount > 1 ? 's' : ''}.</span>
            )}
            <br /><br />
            Cette action est irréversible.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button 
            onClick={handleCloseDeleteDialog} 
            disabled={deleting}
            color="inherit"
          >
            Annuler
          </Button>
          <Button 
            onClick={handleConfirmDelete} 
            disabled={deleting}
            color="error" 
            variant="contained"
            autoFocus
            startIcon={deleting ? <CircularProgress size={20} /> : <Delete />}
          >
            {deleting ? 'Suppression...' : 'Supprimer définitivement'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Dialog de partage */}
      <Dialog
        open={shareDialogOpen}
        onClose={handleCloseShareDialog}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Share color="primary" />
            Partager le quiz
          </Box>
        </DialogTitle>
        <DialogContent>
          {selectedQuiz && (
            <>
              <Box sx={{ mb: 3, p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
                <Typography variant="h6" gutterBottom>
                  {selectedQuiz.title}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {selectedQuiz.description}
                </Typography>
                <Box sx={{ mt: 1, display: 'flex', gap: 1 }}>
                  <Chip label={`${selectedQuiz.questions?.length || 0} questions`} size="small" />
                  <Chip label={`${selectedQuiz.duration} min`} size="small" variant="outlined" />
                  <Chip label={`Note de passage: ${selectedQuiz.passingScore}%`} size="small" variant="outlined" />
                </Box>
              </Box>

              <DialogContentText sx={{ mb: 2 }}>
                Partagez ce lien avec vos étudiants. Ils pourront voir les détails du quiz et devront se connecter ou créer un compte pour le passer.
              </DialogContentText>
              
              <Box sx={{ mb: 2 }}>
                <Typography variant="subtitle2" gutterBottom>
                  Lien du quiz :
                </Typography>
                <Box sx={{ 
                  display: 'flex', 
                  alignItems: 'center', 
                  gap: 1,
                  p: 1.5,
                  border: '2px solid',
                  borderColor: 'primary.main',
                  borderRadius: 1,
                  backgroundColor: alpha(theme.palette.primary.main, 0.05)
                }}>
                  <Typography 
                    variant="body2" 
                    data-link-text
                    sx={{ 
                      flex: 1, 
                      wordBreak: 'break-all',
                      fontFamily: 'monospace',
                      fontSize: '0.9rem',
                      userSelect: 'all',
                      cursor: 'text'
                    }}
                  >
                    {telegramLink}
                  </Typography>
                  <Button
                    onClick={handleCopyLink}
                    size="small"
                    variant="contained"
                    color="primary"
                    startIcon={<CopyIcon />}
                    sx={{ minWidth: 'auto', px: 1.5 }}
                  >
                    Copier
                  </Button>
                </Box>
              </Box>

              <Divider sx={{ my: 2 }} />

              <Box sx={{ mb: 2 }}>
                <Typography variant="subtitle2" gutterBottom>
                  Message prêt à partager :
                </Typography>
                <Paper
                  data-message-text
                  sx={{
                    p: 2,
                    bgcolor: 'grey.50',
                    border: '1px solid',
                    borderColor: 'divider',
                    borderRadius: 1,
                    fontFamily: 'monospace',
                    fontSize: '0.85rem',
                    whiteSpace: 'pre-wrap',
                    maxHeight: 200,
                    overflow: 'auto',
                    userSelect: 'all',
                    cursor: 'text'
                  }}
                >
{`🎯 **${selectedQuiz.title}**

📝 ${selectedQuiz.description || 'Testez vos connaissances !'}

📊 Informations :
• ${selectedQuiz.questions?.length || 0} questions
• Durée : ${selectedQuiz.duration} minutes
• Note de passage : ${selectedQuiz.passingScore}%
• Tentatives autorisées : ${selectedQuiz.maxAttempts}

🔗 Passer le quiz :
${telegramLink}

✅ Créez votre compte gratuit et commencez dès maintenant !`}
                </Paper>
              </Box>

              <Alert severity="info" sx={{ mt: 2 }}>
                💡 <strong>Comment partager :</strong><br />
                1. Cliquez sur "Copier le message complet"<br />
                2. Collez-le dans votre groupe Telegram, WhatsApp, email, etc.<br />
                3. Les étudiants cliquent sur le lien et s'inscrivent/se connectent<br />
                4. Ils passent le quiz directement
              </Alert>
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseShareDialog} color="inherit">
            Fermer
          </Button>
          <Button 
            onClick={handleCopyLink}
            variant="outlined"
            startIcon={<CopyIcon />}
          >
            Copier le lien
          </Button>
          <Button 
            onClick={handleCopyMessage}
            variant="contained"
            startIcon={<CopyIcon />}
            color="primary"
          >
            Copier le message complet
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default QuizList;